import type { BrandStoragePool } from '@/types/package';
import { storageCoveredByPool } from '@/utils/storageCoverage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { StorageStats } from '@/types/media-library';

/**
 * StorageLimitBanner
 *
 * Non-blocking "prime" notice shown at the top of the Media Library when a
 * team is over its storage limit (STORAGE_ENFORCEMENT='warn' mode — uploads
 * still succeed, see storage-quota.service.ts on the backend).
 *
 * - Amber, not red — this is a nudge, not a hard error.
 * - Dismissible for the current session only (re-appears on next page load).
 * - Never blocks any interaction; purely informational.
 */

export interface StorageLimitBannerProps {
  /** Storage stats from useMediaStats() — compared against the team's max_storage limit. */
  stats: StorageStats;
  /** usePackage().getLimit — returns the max_storage limit in MB, or null when unlimited. */
  getLimit: (feature: string) => number | null;
  /** usePackage().storagePool: set for a brand inside a Brand Portfolio (its overflow is absorbed by the pool). */
  pool?: BrandStoragePool;
}

export const StorageLimitBanner = ({ stats, getLimit, pool }: StorageLimitBannerProps) => {
  const { t } = useTranslation('common');
  const [dismissed, setDismissed] = useState(false);

  const limitMB = getLimit('max_storage');
  // null limit = unlimited plan — never show the banner.
  // limitMB === 0 means package data hasn't loaded yet (usePackage.getLimit
  // returns 0 while `data` is undefined) — never a real plan limit, so treat
  // it the same as "don't show" to avoid a false-positive flash on load.
  const isOverLimit = limitMB !== null && limitMB > 0 && stats.totalSize > limitMB * 1024 * 1024
    // Over the brand's own storage but the Brand Portfolio pool covers it: uploads succeed, no warning.
    && !storageCoveredByPool(stats.totalSize / (1024 * 1024), limitMB, pool);

  if (!isOverLimit || dismissed) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
      role="alert"
      aria-live="polite"
    >
      <Icon
        name={ICONS.WARNING}
        size="sm"
        color="text-amber-600"
        className="flex-shrink-0 mt-0.5"
        ariaHidden
      />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">
          {t('storage_over_limit_title')}
        </p>
        <p className="mt-0.5 text-xs sm:text-sm text-amber-700 leading-snug">
          {t('storage_over_limit_body')}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 p-1 rounded text-amber-600 hover:text-amber-900 hover:bg-amber-100 transition-colors"
        aria-label={t('dismiss')}
      >
        <Icon name={ICONS.CROSS} size="xs" ariaHidden />
      </button>
    </div>
  );
};
